/**
 * How the register of risks and issues is read for the screen
 * (docs/plans/2026-10-governance-risks-timeline-secrets.md, "Risks and issues
 * (D53)"): the words for a rating, a status and a date, the register grouped
 * and filtered, the markers on roadmap items and tasks, what the raise dialog
 * may send, and what a person can pick under "Threatens".
 *
 * DOM-free and tested (test/risksModel.test.ts). Only types come from
 * `shared/schemas/projectRisks.ts` and `shared/risks/view.ts` — the first
 * pulls in zod and the second imports from a file that does, neither of which
 * the page carries — so the few rules needed here are restated, smaller. The
 * definitions of record are `riskRating`, `isOpenEntry`, `statusesFor`,
 * `byUrgency`, `riskProblem` and `riskExposure` there.
 */
// Console: the desktop's `ProjectRiskDraft` (shared/protocol/shell.ts) restated over the platform's RiskDraft, without the desktop's shell protocol.
import type { RiskDraft } from '@aioe/shared';
import { dateMedium } from '@aioe/shared/wording/dateTime';
type ProjectRiskDraft = Pick<RiskDraft, 'kind' | 'title'> & Partial<Omit<RiskDraft, 'kind' | 'title'>>;
// Functions only: a tsx test cannot value-import a shared constant (it comes through as CommonJS).
import { assessmentFields, impactForSeverity, reviewDueAfter } from '@aioe/shared/risks/standard';
import type { RiskEntryView } from '@aioe/shared/risks/view';
import type {
  BugSeverity,
  RiskAssessment,
  RiskControl,
  RiskEntry,
  RiskExposure,
  RiskKind,
  RiskLevel,
  RiskPatch,
  RiskRating,
  RiskRoadmapLink,
  RiskStatus,
  RiskTaskLink,
  RiskTemplate,
} from '@aioe/shared';
import { atStage, type StageId } from './riskWorkflowModel.ts';

export type People = Record<string, { name: string; email?: string }>;

// ── The contract's rules, restated ───────────────────────────────────────

export const RISK_LEVELS: readonly RiskLevel[] = ['low', 'medium', 'high'];
export const RATINGS: readonly RiskRating[] = ['critical', 'high', 'medium', 'low'];
const RISK_STATUSES: readonly RiskStatus[] = ['open', 'analysed', 'mitigating', 'monitoring', 'escalated', 'accepted', 'occurred', 'closed'];
const ISSUE_STATUSES: readonly RiskStatus[] = ['open', 'in-progress', 'resolved', 'wont-fix'];
const BUG_STATUSES: readonly RiskStatus[] = ['open', 'in-progress', 'fixed', 'wont-fix', 'duplicate'];
const OPEN_STATUSES: ReadonlySet<RiskStatus> = new Set(['open', 'mitigating', 'in-progress', 'analysed', 'monitoring', 'escalated']);
const SCORE: Record<RiskLevel, number> = { low: 1, medium: 2, high: 3 };
const SEVERITY_RATING: Record<BugSeverity, RiskRating> = { blocker: 'critical', critical: 'critical', major: 'high', minor: 'medium', trivial: 'low' };

export const STATUS_TEXT: Record<RiskStatus, string> = {
  open: 'Open',
  mitigating: 'Being mitigated',
  accepted: 'Accepted',
  occurred: 'Happened',
  closed: 'Closed',
  'in-progress': 'Being fixed',
  resolved: 'Resolved',
  'wont-fix': 'Will not fix',
  analysed: 'Analysed',
  monitoring: 'Being monitored',
  escalated: 'Escalated',
  fixed: 'Fixed',
  duplicate: 'Duplicate',
};
/** A status in words for its kind: a risk's first reads "Identified". */
export function statusText(kind: RiskKind, status: RiskStatus): string {
  return kind === 'risk' && status === 'open' ? 'Identified' : STATUS_TEXT[status];
}
export const RATING_TEXT: Record<RiskRating, string> = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };
export const LEVEL_TEXT: Record<RiskLevel, string> = { low: 'Low', medium: 'Medium', high: 'High' };
export const KIND_TEXT: Record<RiskKind, string> = { risk: 'Risk', issue: 'Issue', bug: 'Bug' };

/** The statuses an entry of this kind may take. */
export function statusesFor(kind: RiskKind): readonly RiskStatus[] {
  return kind === 'risk' ? RISK_STATUSES : kind === 'bug' ? BUG_STATUSES : ISSUE_STATUSES;
}

/** Still needs attention: a risk analysed, treated, watched or escalated; an issue or a bug not yet settled. */
export function isOpenStatus(status: RiskStatus): boolean {
  return OPEN_STATUSES.has(status);
}

type RatedLike = { kind: RiskKind; impact: RiskLevel; likelihood?: RiskLevel; inherent?: Pick<RiskAssessment, 'rating'>; residual?: Pick<RiskAssessment, 'rating'>; severity?: BugSeverity };

/** A risk assessed on a template: its residual's rating, else its inherent's; else likelihood × impact (unknown likelihood counts as medium). An issue: its impact, one step up. A bug: its severity. */
export function ratingOf(e: RatedLike): RiskRating {
  if (e.kind === 'risk') {
    const assessed = e.residual?.rating ?? e.inherent?.rating;
    if (assessed) return assessed;
  }
  if (e.kind === 'bug' && e.severity) return SEVERITY_RATING[e.severity];
  const impact = SCORE[e.impact];
  if (e.kind !== 'risk') return impact === 3 ? 'critical' : impact === 2 ? 'high' : 'medium';
  const score = impact * SCORE[e.likelihood ?? 'medium'];
  return score >= 9 ? 'critical' : score >= 6 ? 'high' : score >= 3 ? 'medium' : 'low';
}

/** Open entries, counted by rating. */
export function exposureOf(entries: ReadonlyArray<RatedLike & { status: RiskStatus }>): RiskExposure {
  const open: Record<RiskRating, number> = { critical: 0, high: 0, medium: 0, low: 0 };
  let risks = 0;
  let issues = 0;
  let bugs = 0;
  for (const e of entries) {
    if (!isOpenStatus(e.status)) continue;
    open[ratingOf(e)]++;
    if (e.kind === 'risk') risks++;
    else if (e.kind === 'bug') bugs++;
    else issues++;
  }
  const worst = RATINGS.find((r) => open[r] > 0);
  return { open, risks, issues, ...(bugs ? { bugs } : {}), ...(worst ? { worst } : {}) };
}

// ── A row of the register ────────────────────────────────────────────────

/** A link as the register shows it. `found` undefined: not known from here (another repository's item, from a repository's view). */
export interface RiskLinkView {
  kind: 'roadmap' | 'task';
  rootId: string;
  /** The item's id (`R-2`, `D4`) or the task's number (`T-4`), when known. */
  label: string;
  title?: string;
  repositoryName?: string;
  found?: boolean;
  /** The task's stable uid and its board (tasks only). */
  uid?: string;
  board?: string;
  /** The roadmap item's id (roadmap only). */
  id?: string;
}

/** One entry with what the register shows beside it. A `RiskEntryView` from the desktop reads as one directly ({@link rowFromView}). */
export interface RiskRow {
  entry: RiskEntry;
  rating: RiskRating;
  open: boolean;
  repositoryName?: string;
  links: RiskLinkView[];
  fromRisk?: { uid: string; id: string; title: string };
}

/** A desktop entry (`projectRisks.get`), every link resolved against the project's map. */
export function rowFromView(v: RiskEntryView): RiskRow {
  const { rating, open, repositoryName, roadmapLinks, taskLinks, fromRiskEntry, ...entry } = v;
  return {
    entry: entry as RiskEntry,
    rating,
    open,
    ...(repositoryName ? { repositoryName } : {}),
    links: [
      ...(roadmapLinks ?? []).map((l): RiskLinkView => ({ kind: 'roadmap', rootId: l.rootId, id: l.id, label: l.id, found: l.found, ...(l.title ? { title: l.title } : {}), ...(l.repositoryName ? { repositoryName: l.repositoryName } : {}) })),
      ...(taskLinks ?? []).map((l): RiskLinkView => ({ kind: 'task', rootId: l.rootId, uid: l.uid, board: l.board, label: l.taskId ?? 'task', found: l.found, ...(l.title ? { title: l.title } : {}), ...(l.repositoryName ? { repositoryName: l.repositoryName } : {}) })),
    ],
    ...(fromRiskEntry ? { fromRisk: { uid: fromRiskEntry.uid, id: fromRiskEntry.id, title: fromRiskEntry.title } } : {}),
  };
}

/** What a repository's view knows to resolve a link against: its own roadmap rows and its board's tasks. */
export interface RepositoryLookup {
  /** This repository's id in the project. */
  rootId: string;
  /** Undefined until the roadmap has been read: nothing is called missing before then. */
  roadmap?: ReadonlyArray<{ id: string; title: string }>;
  tasks?: ReadonlyArray<{ uid?: string; id: string; title: string }>;
}

/**
 * The team that raised each entry, by name, when entries in view were raised by more than one team (D239, C-10);
 * nothing when one team (or none) raised them all, so a single team's Watchlist carries no repeated label. A team
 * with no name recorded reads as "a team", never its id.
 */
export function raisingTeamWords(entries: readonly Pick<RiskEntry, 'raisedByTeam'>[]): (entry: Pick<RiskEntry, 'raisedByTeam'>) => string | undefined {
  const teams = new Set(entries.map((e) => e.raisedByTeam?.id).filter((id): id is string => !!id));
  if (teams.size < 2) return () => undefined;
  return (entry) => (entry.raisedByTeam ? `raised by ${entry.raisedByTeam.name?.trim() || 'a team'}` : undefined);
}

/** An entry from the host's `risks-state`, resolved against what this repository's view knows. */
export function rowFromEntry(entry: RiskEntry, lookup: RepositoryLookup, all: readonly RiskEntry[] = []): RiskRow {
  const mine = (rootId: string) => rootId === lookup.rootId;
  const from = entry.fromRisk ? all.find((e) => e.uid === entry.fromRisk) : undefined;
  return {
    entry,
    rating: ratingOf(entry),
    open: isOpenStatus(entry.status),
    links: [
      ...(entry.roadmap ?? []).map((l): RiskLinkView => {
        const row = mine(l.rootId) ? lookup.roadmap?.find((r) => r.id === l.id) : undefined;
        const found = mine(l.rootId) && lookup.roadmap ? !!row : undefined;
        return { kind: 'roadmap', rootId: l.rootId, id: l.id, label: l.id, ...(l.rootId === '' ? { repositoryName: 'project' } : {}), ...(row ? { title: row.title } : {}), ...(found !== undefined ? { found } : {}) };
      }),
      ...(entry.tasks ?? []).map((l): RiskLinkView => {
        const task = mine(l.rootId) ? lookup.tasks?.find((t) => t.uid === l.uid) : undefined;
        const found = mine(l.rootId) && lookup.tasks?.length ? !!task : undefined;
        return { kind: 'task', rootId: l.rootId, uid: l.uid, board: l.board, label: task?.id ?? 'task', ...(l.rootId === '' ? { repositoryName: 'project' } : {}), ...(task ? { title: task.title } : {}), ...(found !== undefined ? { found } : {}) };
      }),
    ],
    ...(from ? { fromRisk: { uid: from.uid, id: from.id, title: from.title } } : {}),
  };
}

/** Most pressing first: open before settled, then by rating, then the oldest. */
export function byUrgency(a: RiskRow, b: RiskRow): number {
  const open = Number(b.open) - Number(a.open);
  if (open) return open;
  const rating = RATINGS.indexOf(a.rating) - RATINGS.indexOf(b.rating);
  return rating || a.entry.raisedAt.localeCompare(b.entry.raisedAt);
}

// ── Filters and grouping ─────────────────────────────────────────────────

export type KindFilter = 'all' | 'risk' | 'issue' | 'bug';
export type StateFilter = 'open' | 'settled' | 'all';
/** Something an entry threatens, to show only those: a roadmap item, or a task by its uid. */
export type ThreatFilter = { kind: 'roadmap'; rootId: string; id: string; label?: string } | { kind: 'task'; uid: string; label?: string };

export interface RiskFilter {
  kind: KindFilter;
  state: StateFilter;
  /** One repository's ("" = the project's own); undefined = every one. */
  repository?: string;
  threatens?: ThreatFilter;
  /** A heatmap cell pressed: only the risks placed in it. */
  cell?: { likelihood: number; impact: number; uids: readonly string[]; label: string };
  /** Only those carrying this tag (any case). */
  tag?: string;
  /** Only those at one step of their lifecycle (the Watchlist picture's "Show me"). */
  stage?: { kind: RiskKind; stage: StageId; label: string };
}

export const DEFAULT_FILTER: RiskFilter = { kind: 'all', state: 'all' };

export function threatens(row: RiskRow, t: ThreatFilter): boolean {
  return t.kind === 'roadmap' ? row.links.some((l) => l.kind === 'roadmap' && l.rootId === t.rootId && l.id === t.id) : row.links.some((l) => l.kind === 'task' && l.uid === t.uid);
}

export function applyFilter(rows: readonly RiskRow[], f: RiskFilter): RiskRow[] {
  return rows.filter(
    (r) =>
      (f.kind === 'all' || r.entry.kind === f.kind) &&
      (f.state === 'all' || (f.state === 'open') === r.open) &&
      (f.repository === undefined || r.entry.rootId === f.repository) &&
      (!f.threatens || threatens(r, f.threatens)) &&
      (!f.cell || f.cell.uids.includes(r.entry.uid)) &&
      (!f.tag || (r.entry.tags ?? []).some((t) => t.toLowerCase() === f.tag!.toLowerCase())) &&
      (!f.stage || atStage(r.entry, f.stage.kind, f.stage.stage)),
  );
}

/** The tags used in the register, each once (the first spelling), most used first, then by name — for the tag filter and the tag field's suggestions. */
export function tagsInUse(rows: ReadonlyArray<Pick<RiskRow, 'entry'>>): Array<{ tag: string; count: number }> {
  const seen = new Map<string, { tag: string; count: number }>();
  for (const r of rows)
    for (const t of r.entry.tags ?? []) {
      const key = t.trim().toLowerCase();
      if (!key) continue;
      const hit = seen.get(key);
      if (hit) hit.count++;
      else seen.set(key, { tag: t.trim(), count: 1 });
    }
  return [...seen.values()].sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
}

/** The register as it is shown: open first, then the settled, each most pressing first. */
export function groupRows(rows: readonly RiskRow[], f: RiskFilter): { open: RiskRow[]; settled: RiskRow[] } {
  const shown = applyFilter(rows, f).sort(byUrgency);
  return { open: shown.filter((r) => r.open), settled: shown.filter((r) => !r.open) };
}

// ── Words ────────────────────────────────────────────────────────────────

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "2 critical, 1 high", or "nothing open". */
export function ratingBreakdown(open: Record<RiskRating, number>): string {
  const parts = RATINGS.filter((r) => open[r] > 0).map((r) => `${open[r]} ${r}`);
  return parts.length ? parts.join(', ') : 'nothing open';
}

/** Home's line: "3 open risks and 1 issue — the worst is critical" (and bugs, when there are any). Empty when nothing is open. */
export function exposureSentence(e: RiskExposure): string {
  const bugs = e.bugs ?? 0;
  const parts = [e.risks ? plural(e.risks, 'risk', 'risks') : '', e.issues ? plural(e.issues, 'issue', 'issues') : '', bugs ? plural(bugs, 'bug', 'bugs') : ''].filter(Boolean);
  if (!parts.length) return '';
  const joined = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  const what = `${joined.replace(/^(\d+) /, '$1 open ')}`;
  return e.worst ? `${what} — the worst is ${e.worst}` : what;
}

const DAY_MS = 86_400_000;

function dateOnly(iso: string): number | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return undefined;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function shortDate(ms: number, withYear: boolean): string {
  return dateMedium(ms, { year: withYear, timeZone: 'UTC' });
}

/** A due date in words, and whether it has passed while the entry is still open. */
export function dueWords(due: string | undefined, open: boolean, now: number): { text: string; overdue: boolean } | undefined {
  if (!due) return undefined;
  const at = dateOnly(due);
  if (at === undefined) return { text: `due ${due}`, overdue: false };
  const d = new Date(now);
  const today = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const withYear = new Date(at).getUTCFullYear() !== d.getFullYear();
  const overdue = open && at < today;
  if (overdue) {
    const days = Math.round((today - at) / DAY_MS);
    return { text: days === 1 ? 'overdue since yesterday' : `overdue since ${shortDate(at, withYear)}`, overdue };
  }
  if (open && at === today) return { text: 'due today', overdue: false };
  return { text: `due ${shortDate(at, withYear)}`, overdue: false };
}

/** A name signed as a computer account, with its space back: the shared rule (shared/people/names.ts), re-exported for the record line. */
export { readableName } from '@aioe/shared/people/names';

/**
 * Who an entry's `actor` is, in words (D239, B-13): an agent by its name (`agent:Kai` → "Kai", with its team when
 * the entry records one), never the raw `agent:` key, a session or a GUID; undefined when it names no one readable.
 */
export function actorWords(actor: string | undefined, team?: { name?: string }): string | undefined {
  const raw = actor?.trim();
  if (!raw) return undefined;
  const name = raw.startsWith('agent:') ? raw.slice('agent:'.length).trim() : raw;
  if (!name || /^[0-9a-f-]{20,}$/i.test(name) || /^(agt|ses|s|usr|hp|prn)[_:-][A-Za-z0-9_-]+$/.test(name) || name.includes(':')) return undefined;
  const teamName = team?.name?.trim();
  return teamName ? `${name} (${teamName})` : name;
}

/** A person's name from the register's list; a free-text owner as written. */
export function ownerName(owner: string | undefined, people: People, ownerPerson?: { id?: string; name?: string } | null): string | undefined {
  // The home puts the owner as a person on every answer (AIOE 0.35.0, D249): read that first, never an id.
  const named = ownerPerson?.name?.trim();
  if (named) return named;
  const o = owner?.trim();
  if (!o) return undefined;
  return people[o]?.name?.trim() || o;
}

/** What an owner typed in the dialog is stored as: a person's id when the name is someone's, else the name. */
export function ownerValue(typed: string, people: People): string | undefined {
  const t = typed.trim();
  if (!t) return undefined;
  const hit = Object.entries(people).find(([, p]) => p.name.trim().toLowerCase() === t.toLowerCase());
  return hit ? hit[0] : t;
}

/** A link's chip: its id, and what its hint says. */
export function linkWords(link: RiskLinkView): { label: string; hint: string; missing: boolean } {
  const where = link.repositoryName ? `${link.repositoryName} ` : '';
  const what = link.kind === 'roadmap' ? 'roadmap item' : 'task';
  if (link.found === false) return { label: `${where}${link.label}`, hint: `This ${what} is no longer there`, missing: true };
  return { label: `${where}${link.label}`, hint: link.title ? `${link.label} ${link.title}` : link.kind === 'roadmap' ? `Roadmap item ${link.label}` : 'A task on another board', missing: false };
}

// ── Markers on what an entry threatens ───────────────────────────────────

export interface RiskMarker {
  open: number;
  worst: RiskRating;
  /** "2 open: RK-3 critical, IS-1 high". */
  text: string;
  ids: string[];
}

/** The marker for one roadmap item or task: only when an open entry threatens it. */
export function markerFor(rows: readonly RiskRow[], t: ThreatFilter): RiskMarker | undefined {
  const hits = rows.filter((r) => r.open && threatens(r, t)).sort(byUrgency);
  if (!hits.length) return undefined;
  const list = hits.map((r) => `${r.entry.id} ${r.rating}`);
  return { open: hits.length, worst: hits[0].rating, ids: hits.map((r) => r.entry.id), text: `${hits.length} open: ${list.slice(0, 4).join(', ')}${list.length > 4 ? `, and ${list.length - 4} more` : ''}` };
}

/** A marker from the desktop's roll-up alone (no entries read): "2 open risks and issues, the worst high". */
export function markerFromMark(mark: { open: number; worst?: RiskRating } | undefined): RiskMarker | undefined {
  if (!mark || mark.open <= 0 || !mark.worst) return undefined;
  return { open: mark.open, worst: mark.worst, ids: [], text: `${mark.open} open ${mark.open === 1 ? 'risk or issue' : 'risks and issues'}, the worst ${mark.worst}` };
}

// ── The raise dialog ─────────────────────────────────────────────────────

/** One assessment as the form holds it: a level per dimension (`overall` among them); 0 = not rated. */
export interface AssessmentInput {
  likelihood: number;
  impacts: Record<string, number>;
}

export interface RiskForm {
  kind: RiskKind;
  title: string;
  description: string;
  /** The three steps, for an issue's impact and a risk with no assessment on the template. */
  likelihood: RiskLevel;
  impact: RiskLevel;
  owner: string;
  /** The owner's id at the project's home, when the picker chose an organisation person (D247): stored over the name. */
  ownerId?: string;
  response: string;
  due: string;
  roadmap: RiskRoadmapLink[];
  tasks: RiskTaskLink[];
  fromRisk?: string;
  // ── D117 ──
  status: RiskStatus;
  category: string;
  disposition: '' | NonNullable<RiskEntry['disposition']>;
  confidentiality: NonNullable<RiskEntry['confidentiality']>;
  identifiedOn: string;
  reviewDue: string;
  /** Chosen in the tag field (ui/TagInput.tsx): tidied, no repeats, at most 20. */
  tags: string[];
  /** A risk: before any control; likelihood 0 = not assessed on the template. */
  inherent: AssessmentInput;
  /** A risk: `controls` — the residual follows the controls; `assessed` — set by hand below. */
  residualMode: 'controls' | 'assessed';
  residual: AssessmentInput;
  controls: RiskControl[];
  severity: '' | BugSeverity;
  repro: string;
  /** One area or file per line. */
  affects: string;
  foundIn: string;
  pullRequest: string;
  raisedFrom?: RiskEntry['raisedFrom'];
}

const NO_ASSESSMENT: AssessmentInput = { likelihood: 0, impacts: {} };

export function emptyForm(kind: RiskKind = 'risk', now: number = Date.now(), reviewEveryDays = 30): RiskForm {
  return {
    kind,
    title: '',
    description: '',
    likelihood: 'medium',
    impact: 'medium',
    owner: '',
    response: '',
    due: '',
    roadmap: [],
    tasks: [],
    status: 'open',
    category: '',
    disposition: '',
    confidentiality: 'internal',
    identifiedOn: new Date(now).toISOString().slice(0, 10),
    reviewDue: kind === 'risk' ? reviewDueAfter(now, reviewEveryDays) : '',
    tags: [],
    inherent: { ...NO_ASSESSMENT },
    residualMode: 'controls',
    residual: { ...NO_ASSESSMENT },
    controls: [],
    severity: kind === 'bug' ? 'major' : '',
    repro: '',
    affects: '',
    foundIn: '',
    pullRequest: '',
  };
}

const inputOf = (a: RiskAssessment | undefined): AssessmentInput => (a ? { likelihood: a.likelihood, impacts: { ...a.impacts } } : { ...NO_ASSESSMENT });
const rated = (a: AssessmentInput): boolean => a.likelihood > 0 && Object.values(a.impacts).some((n) => n > 0);
const cleanImpacts = (a: AssessmentInput): Record<string, number> => Object.fromEntries(Object.entries(a.impacts).filter(([, n]) => n > 0));

/** An entry as the form edits it. */
export function formFromEntry(e: RiskEntry, people: People = {}): RiskForm {
  return {
    ...emptyForm(e.kind),
    title: e.title,
    description: e.description ?? '',
    likelihood: e.likelihood ?? 'medium',
    impact: e.impact,
    owner: ownerName(e.owner, people, e.ownerPerson) ?? '',
    ...(e.ownerPerson?.id ? { ownerId: e.ownerPerson.id } : e.owner && people[e.owner] ? { ownerId: e.owner } : {}),
    response: e.response ?? '',
    due: e.due?.slice(0, 10) ?? '',
    roadmap: [...(e.roadmap ?? [])],
    tasks: [...(e.tasks ?? [])],
    ...(e.fromRisk ? { fromRisk: e.fromRisk } : {}),
    status: e.status,
    category: e.category ?? '',
    disposition: e.disposition ?? '',
    confidentiality: e.confidentiality ?? 'internal',
    identifiedOn: e.identifiedOn?.slice(0, 10) ?? '',
    reviewDue: e.reviewDue?.slice(0, 10) ?? '',
    tags: [...(e.tags ?? [])],
    inherent: inputOf(e.inherent),
    residualMode: e.residual?.basis === 'assessed' ? 'assessed' : 'controls',
    residual: inputOf(e.residual),
    controls: [...(e.controls ?? [])],
    severity: e.severity ?? '',
    repro: e.repro ?? '',
    affects: (e.affects ?? []).join('\n'),
    foundIn: e.foundIn ?? '',
    pullRequest: e.pullRequest ?? '',
    ...(e.raisedFrom ? { raisedFrom: e.raisedFrom } : {}),
  };
}

/** The assessment fields the form adds up to on the template: inherent and residual derived, the three steps kept in step. */
export function formAssessment(f: RiskForm, t: RiskTemplate, at?: string, before: Partial<Pick<RiskEntry, 'inherent' | 'residual'>> = {}): Partial<Pick<RiskEntry, 'inherent' | 'residual' | 'likelihood' | 'impact'>> {
  if (f.kind !== 'risk' || !rated(f.inherent)) return {};
  const residual = f.residualMode === 'assessed' && rated(f.residual) ? { likelihood: f.residual.likelihood, impacts: cleanImpacts(f.residual), basis: 'assessed' as const } : null;
  // `before` only tells it where the entry stood (its band), so a moved band is recorded as `was`; the form says everything else.
  return assessmentFields({ inherent: { likelihood: f.inherent.likelihood, impacts: cleanImpacts(f.inherent) }, residual, controls: f.controls }, { ...(before.inherent ? { inherent: before.inherent } : {}), ...(before.residual ? { residual: before.residual } : {}) }, t, at);
}

const list = (text: string, split: RegExp, max: number, len: number) =>
  text
    .split(split)
    .map((x) => x.trim())
    .filter(Boolean)
    .slice(0, max)
    .map((x) => x.slice(0, len));

/** Why the form cannot be sent, in words; undefined when it can. */
export function formProblem(f: Pick<RiskForm, 'kind' | 'title' | 'fromRisk'>): string | undefined {
  if (!f.title.trim()) return f.kind === 'risk' ? 'Say in a line what may go wrong.' : 'Say in a line what has gone wrong.';
  if (f.title.trim().length > 200) return 'Keep the title to 200 characters; the rest goes in the description.';
  if (f.kind === 'risk' && f.fromRisk) return 'Only an issue names the risk it came from.';
  return undefined;
}

/**
 * What the dialog sends: an issue or a bug carries no likelihood, a risk's
 * assessment is derived on the template (and its three steps follow it), a
 * bug's impact follows its severity, empty fields are left out.
 */
export function draftFromForm(f: RiskForm, people: People, t?: RiskTemplate, before?: Partial<Pick<RiskEntry, 'inherent' | 'residual'>>): ProjectRiskDraft {
  const owner = f.owner.trim() && f.ownerId ? f.ownerId : ownerValue(f.owner, people);
  const assessed = t ? formAssessment(f, t, new Date().toISOString(), before) : {};
  const tags = list(f.tags.join(','), /,/, 20, 40);
  const affects = list(f.affects, /\n/, 50, 300);
  return {
    kind: f.kind,
    title: f.title.trim(),
    impact: f.kind === 'bug' && f.severity ? impactForSeverity(f.severity) : f.impact,
    ...(f.kind === 'risk' ? { likelihood: f.likelihood } : {}),
    ...(f.description.trim() ? { description: f.description.trim() } : {}),
    ...(owner ? { owner } : {}),
    ...(f.response.trim() ? { response: f.response.trim() } : {}),
    ...(f.due ? { due: f.due } : {}),
    ...(f.roadmap.length ? { roadmap: f.roadmap } : {}),
    ...(f.tasks.length ? { tasks: f.tasks } : {}),
    ...(f.kind === 'issue' && f.fromRisk ? { fromRisk: f.fromRisk } : {}),
    ...(f.status !== 'open' && statusesFor(f.kind).includes(f.status) ? { status: f.status } : {}),
    ...(f.category.trim() ? { category: f.category.trim().slice(0, 60) } : {}),
    ...(f.kind === 'risk' && f.disposition ? { disposition: f.disposition } : {}),
    confidentiality: f.confidentiality,
    ...(f.identifiedOn ? { identifiedOn: f.identifiedOn } : {}),
    ...(f.reviewDue ? { reviewDue: f.reviewDue } : {}),
    ...(tags.length ? { tags } : {}),
    ...(f.kind === 'risk' && f.controls.length ? { controls: f.controls } : {}),
    ...(f.kind === 'risk' && t && assessed.inherent ? { ...assessed, templateId: t.id } : {}),
    ...(f.kind === 'bug' && f.severity ? { severity: f.severity } : {}),
    ...(f.kind === 'bug' && f.repro.trim() ? { repro: f.repro.trim() } : {}),
    ...(f.kind === 'bug' && affects.length ? { affects } : {}),
    ...(f.kind === 'bug' && f.foundIn.trim() ? { foundIn: f.foundIn.trim().slice(0, 80) } : {}),
    ...(f.kind === 'bug' && f.pullRequest.trim() ? { pullRequest: f.pullRequest.trim().slice(0, 500) } : {}),
    ...(f.raisedFrom ? { raisedFrom: f.raisedFrom } : {}),
  };
}

/**
 * What saving the form over an entry changes: only what differs, `null` for
 * what was emptied. The assessment is derived again on the template.
 */
export function patchFromForm(e: RiskEntry, f: RiskForm, people: People, t: RiskTemplate): RiskPatch {
  const next = draftFromForm({ ...f, kind: e.kind }, people, t, e) as Record<string, unknown>;
  const before = e as unknown as Record<string, unknown>;
  const same = (k: string) => JSON.stringify(comparable(k, before[k])) === JSON.stringify(comparable(k, next[k]));
  const assessmentChanged = e.kind === 'risk' && !(same('inherent') && same('residual') && same('controls'));
  const patch: Record<string, unknown> = {};
  const KEYS = ['title', 'description', 'likelihood', 'impact', 'owner', 'response', 'due', 'roadmap', 'tasks', 'status', 'category', 'disposition', 'confidentiality', 'identifiedOn', 'reviewDue', 'tags', 'controls', 'inherent', 'residual', 'templateId', 'severity', 'repro', 'affects', 'foundIn', 'pullRequest'] as const;
  for (const k of KEYS) {
    if (k === 'status' && next.status === undefined) {
      if (e.status !== 'open' && f.status === 'open') patch.status = 'open';
      continue;
    }
    if (k === 'likelihood' && e.kind !== 'risk') continue;
    // The three steps and the template follow the assessment: written only when it changed.
    if (e.kind === 'risk' && e.inherent && !assessmentChanged && (k === 'likelihood' || k === 'impact' || k === 'templateId')) continue;
    if (k === 'templateId' && !assessmentChanged) continue;
    // Confidentiality left at the form's default on an entry that never had one is not a change.
    if (k === 'confidentiality' && before.confidentiality === undefined && next.confidentiality === 'internal') continue;
    if (same(k)) continue;
    patch[k] = next[k] === undefined ? (k === 'roadmap' || k === 'tasks' ? [] : null) : next[k];
  }
  return patch as RiskPatch;
}

/** A field as saving compares it: an empty list is none, an assessment without its stamp, its notes, or (inherent) its basis. */
function comparable(key: string, v: unknown): unknown {
  if (Array.isArray(v)) return v.length ? v : undefined;
  if (!v || typeof v !== 'object' || !('likelihood' in (v as object))) return v;
  const { assessedAt: _a, notes: _n, was: _w, basis, ...rest } = v as Record<string, unknown>;
  return key === 'residual' ? { ...rest, basis: basis ?? 'controls' } : rest;
}

/** "It happened": an issue that names the risk, its title and impact kept, and what it threatened. */
export function issueFromRisk(risk: RiskEntry): RiskForm {
  return { ...emptyForm('issue'), title: risk.title, impact: risk.impact, owner: risk.owner ?? '', roadmap: [...(risk.roadmap ?? [])], tasks: [...(risk.tasks ?? [])], fromRisk: risk.uid, ...(risk.category ? { category: risk.category } : {}) };
}

/** The overall impact the form holds (its highest dimension), 0 when none. */
export function formOverall(a: AssessmentInput): number {
  const values = Object.values(a.impacts).filter((n) => n > 0);
  return values.length ? Math.max(...values) : 0;
}

/** The dimension every template has (shared/risks/standard.ts `OVERALL`). */
export const OVERALL = 'overall';

// ── What can be picked under "Threatens" ─────────────────────────────────

export interface LinkChoice {
  key: string;
  kind: 'roadmap' | 'task';
  /** `R-2`, `D4`, `T-3`. */
  label: string;
  title: string;
  /** The repository it is in; absent for the project's own. */
  repositoryName?: string;
  link: RiskRoadmapLink | RiskTaskLink;
}

export function roadmapKey(l: RiskRoadmapLink): string {
  return `r\u0000${l.rootId}\u0000${l.id}`;
}

export function taskKey(l: Pick<RiskTaskLink, 'uid'>): string {
  return `t\u0000${l.uid}`;
}

/** The form's links as the picker's keys. */
export function selectedKeys(f: Pick<RiskForm, 'roadmap' | 'tasks'>): string[] {
  return [...f.roadmap.map(roadmapKey), ...f.tasks.map(taskKey)];
}

/** The picker's keys back into links, against what it offered (a key it no longer offers keeps its link). */
export function linksFromKeys(keys: readonly string[], choices: readonly LinkChoice[], before: Pick<RiskForm, 'roadmap' | 'tasks'>): Pick<RiskForm, 'roadmap' | 'tasks'> {
  const known = new Map(choices.map((c) => [c.key, c]));
  for (const l of before.roadmap) if (!known.has(roadmapKey(l))) known.set(roadmapKey(l), { key: roadmapKey(l), kind: 'roadmap', label: l.id, title: '', link: l });
  for (const l of before.tasks) if (!known.has(taskKey(l))) known.set(taskKey(l), { key: taskKey(l), kind: 'task', label: 'a task', title: '', link: l });
  const picked = keys.map((k) => known.get(k)).filter((c): c is LinkChoice => !!c);
  return { roadmap: picked.filter((c) => c.kind === 'roadmap').map((c) => c.link as RiskRoadmapLink), tasks: picked.filter((c) => c.kind === 'task').map((c) => c.link as RiskTaskLink) };
}

/** The project's own roadmap and tasks, and what each repository last mirrored, from `projectWork.get`. */
export interface ProjectWorkLike {
  roadmap: { rows: ReadonlyArray<{ id: string; title: string }> };
  tasks: ReadonlyArray<{ id: string; uid: string; title: string; effectiveStatus?: string }>;
  repositories: ReadonlyArray<{ rootId: string; name: string }>;
  map: { roots: ReadonlyArray<{ rootId: string; roadmap?: ReadonlyArray<{ id: string; title: string }>; boards?: ReadonlyArray<{ board: string; tasks: ReadonlyArray<{ id: string; uid?: string; title: string; status?: string }> }> }> };
}

/** Everything in the project an entry can threaten: its own roadmap and tasks first, then each repository's. */
export function choicesFromProject(w: ProjectWorkLike): LinkChoice[] {
  const name = (rootId: string) => w.repositories.find((r) => r.rootId === rootId)?.name ?? rootId;
  const out: LinkChoice[] = [];
  for (const r of w.roadmap.rows) out.push({ key: roadmapKey({ rootId: '', id: r.id }), kind: 'roadmap', label: r.id, title: r.title, link: { rootId: '', id: r.id } });
  for (const root of w.map.roots) for (const r of root.roadmap ?? []) out.push({ key: roadmapKey({ rootId: root.rootId, id: r.id }), kind: 'roadmap', label: r.id, title: r.title, repositoryName: name(root.rootId), link: { rootId: root.rootId, id: r.id } });
  for (const t of w.tasks) out.push({ key: taskKey(t), kind: 'task', label: t.id, title: t.title, link: { rootId: '', board: 'project', uid: t.uid } });
  for (const root of w.map.roots)
    for (const b of root.boards ?? []) for (const t of b.tasks) if (t.uid) out.push({ key: taskKey({ uid: t.uid }), kind: 'task', label: t.id, title: t.title, repositoryName: name(root.rootId), link: { rootId: root.rootId, board: b.board, uid: t.uid } });
  return out;
}

/** What one repository's view can offer: its roadmap rows and its board's tasks (a task without a uid cannot be named). */
export function choicesFromRepository(rootId: string, board: string, roadmap: ReadonlyArray<{ id: string; title: string }>, tasks: ReadonlyArray<{ uid?: string; id: string; title: string; archivedAt?: string }>): LinkChoice[] {
  return [
    ...roadmap.map((r): LinkChoice => ({ key: roadmapKey({ rootId, id: r.id }), kind: 'roadmap', label: r.id, title: r.title, link: { rootId, id: r.id } })),
    ...tasks.filter((t) => !!t.uid && !t.archivedAt).map((t): LinkChoice => ({ key: taskKey({ uid: t.uid! }), kind: 'task', label: t.id, title: t.title, link: { rootId, board, uid: t.uid! } })),
  ];
}

/** The choices a query finds: by id, title or repository. */
export function filterChoices(choices: readonly LinkChoice[], query: string): LinkChoice[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...choices];
  return choices.filter((c) => c.label.toLowerCase().includes(q) || c.title.toLowerCase().includes(q) || (c.repositoryName ?? '').toLowerCase().includes(q));
}
